<template>
  <div :class="['c-dropdown', val ? '-visible' : '', block ? '-block' : '']">
    <div
      ref="trigger"
      :class="['_trigger', triggerClass]"
      @click="open"
    >
      <slot />
    </div>
    <teleport
      v-if="val"
      to="#overlay"
    >
      <div
        ref="overlay"
        :class="['_dropdown-overlay', val ? '-visible' : '']"
        @mousedown.stop="hide"
      />
      <div
        class="_trigger-copy"
        :style="triggerCopyStyle"
      >
        <div
          :style="referenceStyle"
        />
        <div
          :style="innerStyle"
        >
          <div
            :style="{ position: 'relative', top: '100%' }"
          >
            <transition
              :name="disableAnimation ? 'noop' : 'dropdown-bounce'"
              mode="out-in"
            >
              <div
                v-if="valInner"
                ref="inner"
                :style="contentStyle"
                class="__inner"
                tabindex="0"
                @click="handleOverlayClick"
                @keydown="$emit('keydown', $event)"
                @mousedown.stop
              >
                <slot name="content" />
              </div>
            </transition>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-unresolved
import { Transition, nextTick } from 'vue';

const distanceFromBorder = 8; // in px
const distanceToTrigger = 2; // in px

export default {
  name: 'CDropdown',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    horizontalAlignment: {
      type: String,
      default: 'center',
    },
    openToTop: {
      type: Boolean,
      default: false,
    },
    overlapping: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    trigger: {
      type: Array,
      default: () => ['hover'],
    },
    block: {
      type: Boolean,
      default: false,
    },
    matchTriggerWidth: {
      type: Boolean,
      default: false,
    },
    closeOnClick: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    contentMayResize: {
      type: Boolean,
      default: false,
    },
    triggerClass: {
      type: String,
      default: '',
    },
    relocateKey: {
      type: [String, Number, Boolean, Array],
      default: '',
    },
    stopTriggerEventPropagation: {
      type: Boolean,
      default: false,
    },
    keepOpenOnMaskClick: {
      type: Boolean,
      default: false,
    },
    fullscreenOnMobile: {
      type: Boolean,
      default: true,
    },
    disableAnimation: {
      type: Boolean,
      default: false,
    },
    getTriggerDimensions: {
      type: Function,
      default: (trigger) => {
        if (trigger === undefined) {
          return null;
        }
        return trigger.getBoundingClientRect();
      },
    },
  },
  emits: ['hide', 'overlay-width-set', 'input', 'update:value', 'change', 'overlay-clicked', 'keydown'],
  components: { Transition },
  data() {
    return {
      val: false,
      valInner: false,
      overlayWidth: 0,
      overlayHeight: 0,
      windowWidth: 0,
      windowHeight: 0,
      contentStyle: { display: 'flex', position: 'absolute', pointerEvents: 'auto' },
      triggerDimensions: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
    };
  },
  computed: {
    innerStyle() {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
      };
    },
    referenceStyle() {
      if (this.overlapping) {
        return { width: `${this.triggerDimensions.width}px`, height: 0 };
      }
      return { width: `${this.triggerDimensions.width}px`, height: `${this.triggerDimensions.height}px` };
    },
    triggerCopyStyle() {
      return {
        position: 'fixed',
        top: `${this.triggerDimensions.y}px`,
        left: `${this.triggerDimensions.x}px`,
        pointerEvents: 'none',
      };
    },
  },
  methods: {
    calculateContentStyle() {
      const middlewares = [
        this.resizer,
        this.horizontalAligner,
        this.verticalAligner,
        this.fixVerticalPosition,
        this.fixHorizontalPosition,
      ];
      let res = {
        position: 'relative',
        display: 'flex',
        pointerEvents: 'auto',
        outline: 'none',
      };
      middlewares.forEach((m) => {
        res = m(res);
      });
      this.contentStyle = res;
    },
    horizontalAligner(style) {
      switch (this.horizontalAlignment) {
        case 'left':
          return {
            ...style,
            left: `${(this.overlayWidth / 2) - (this.triggerDimensions.width / 2)}px`,
          };
        case 'right':
          return {
            ...style,
            left: `${-((this.overlayWidth / 2) - (this.triggerDimensions.width / 2))}px`,
          };
        case 'center':
          return {
            ...style,
            left: 0,
          };
        default:
          throw new Error('horizontal alignment option invalid');
      }
    },
    verticalAligner(style) {
      if (this.openToTop) {
        return {
          ...style,
          top: `${-distanceToTrigger - this.overlayHeight}px`,
        };
      }
      if (this.overlapping) {
        return {
          ...style,
          top: 0,
        };
      }
      return {
        ...style,
        top: `${distanceToTrigger}px`,
      };
    },
    resizer(style) {
      if (this.matchTriggerWidth) {
        return {
          ...style,
          width: `${this.triggerDimensions.width}px`,
        };
      }

      return style;
    },
    left() {
      switch (this.horizontalAlignment) {
        case 'left':
          return this.triggerDimensions.x;
        case 'center':
          return this.triggerDimensions.x - ((this.overlayWidth / 2) - (this.triggerDimensions.width / 2));
        case 'right':
          return this.triggerDimensions.x + (this.triggerDimensions.width - this.overlayWidth);
        default:
          return 0;
      }
    },
    right() {
      switch (this.horizontalAlignment) {
        case 'left':
          return this.triggerDimensions.x + this.overlayWidth;
        case 'center':
          return this.triggerDimensions.x + ((this.overlayWidth / 2) - (this.triggerDimensions.width / 2)) + this.triggerDimensions.width;
        case 'right':
          return this.triggerDimensions.x + this.triggerDimensions.width;
        default:
          return 0;
      }
    },
    top() {
      if (this.overlapping) {
        if (this.openToTop) {
          return this.triggerDimensions.y - this.triggerDimensions.height - this.overlayHeight;
        }
        return this.triggerDimensions.y;
      }
      if (this.openToTop) {
        return this.triggerDimensions.y - this.overlayHeight - distanceToTrigger;
      }
      return this.triggerDimensions.y + this.triggerDimensions.height + distanceToTrigger;
    },
    bottom() {
      if (this.overlapping) {
        if (this.openToTop) {
          return this.triggerDimensions.y - this.triggerDimensions.height;
        }
        return this.triggerDimensions.y + this.overlayHeight;
      }
      if (this.openToTop) {
        return this.triggerDimensions.y - this.overlayHeight;
      }
      return this.triggerDimensions.y + this.triggerDimensions.height + this.overlayHeight + distanceToTrigger;
    },
    fixHorizontalPosition(style) {
      const left = this.left();
      const right = this.right();
      if (this.overlayWidth > this.windowWidth) {
        return {
          ...style,
          width: '100vw',
          left: 0,
        };
      }
      if (left < 0) {
        return {
          ...style,
          left: `${parseInt(style.left, 10) + distanceFromBorder + Math.abs(left)}px`,
        };
      }
      if (right > (this.windowWidth - distanceFromBorder)) {
        return {
          ...style,
          left: `${parseInt(style.left, 10) - distanceFromBorder - Math.abs(right - this.windowWidth)}px`,
        };
      }
      return style;
    },
    fixVerticalPosition(style) {
      const bottom = this.bottom();
      const top = this.top();
      if (top < 0 && bottom > this.windowHeight) {
        return {
          ...style,
          width: '100vw',
          top: `${-top}px`,
        };
      }
      if (top < 0) {
        return {
          ...style,
          top: `${parseInt(style.top, 10) - top - distanceFromBorder}px`,
        };
      }
      if (bottom > this.windowHeight) {
        return {
          ...style,
          top: `${parseInt(style.top, 10) - (bottom - this.windowHeight) - distanceFromBorder}px`,
        };
      }
      return style;
    },
    hide() {
      this.$emit('overlay-clicked');
      if (this.keepOpenOnMaskClick) {
        return;
      }
      this.$emit('hide');
      this.valInner = false;
      setTimeout(() => {
        this.val = false;
      }, 200);
    },
    focus() {
      if (this.$refs.inner === null) {
        return;
      }

      this.$refs.inner.focus();
    },
    open(event) {
      if (this.stopTriggerEventPropagation) {
        event.stopPropagation();
      }

      if (this.disabled) {
        return;
      }

      this.val = true;
    },
    calculateWindowSize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },
    handleOverlayClick(event) {
      if (this.closeOnClick) {
        this.hide();
        return;
      }

      event.stopPropagation();
    },
    setTriggerDimensions() {
      const d = this.getTriggerDimensions(this.$refs.trigger);
      if (d === null) {
        return;
      }

      this.triggerDimensions = {
        x: d.x,
        y: d.y,
        width: d.width,
        height: d.height,
      };
    },
    recalculateOverlaySize() {
      this.setOverlaySize();
      this.setTriggerDimensions();
      this.calculateContentStyle();
    },
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.hide();
      }
    },
    setOverlaySize(callNum = 1) {
      if (this.$refs.inner === null || typeof this.$refs.inner === 'undefined') {
        return;
      }
      if (this.$refs.inner.clientWidth === 0 && callNum < 10) {
        nextTick(() => this.setOverlaySize(callNum + 1));
        return;
      }
      this.overlayWidth = this.$refs.inner.clientWidth;
      this.$emit('overlay-width-set', this.overlayWidth);
      if (this.contentMayResize) {
        this.overlayHeight = window.innerHeight / 2 - 100;
        return;
      }
      this.overlayHeight = this.$refs.inner.clientHeight;
    },
  },
  watch: {
    relocateKey() {
      nextTick(() => {
        nextTick(() => {
          this.recalculateOverlaySize();
        });
      });
    },
    val(val) {
      this.$emit('input', val);
      this.$emit('update:value', val);
      this.$emit('change', val);
      this.$nextTick(() => {
        this.valInner = val;
        if (val) {
          this.$nextTick(() => {
            this.setOverlaySize();
            this.calculateContentStyle();
          });
        }
      });
    },
    valInner(val) {
      if (val) {
        this.$nextTick(() => {
          this.recalculateOverlaySize();
          // this.$refs.inner.addEventListener('keydown', this.handleKeyDown);
          // this.$refs.inner.focus();
        });
        return;
      }

      if (typeof this.$refs.inner !== 'undefined' && this.$refs.inner !== null) {
        this.$refs.inner.removeEventListener('keydown', this.handleKeyDown);
      }
    },
    value(val) {
      if (!val) {
        this.val = val;
      }

      if (this.disabled) {
        return;
      }

      this.val = val;
    },
  },
  created() {
    this.val = this.value;
  },
  mounted() {
    this.calculateWindowSize();
    this.setTriggerDimensions();
    window.addEventListener('resize', this.calculateWindowSize);
    window.addEventListener('resize', this.setTriggerDimensions);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateWindowSize);
    window.removeEventListener('resize', this.setTriggerDimensions);
  },
};
</script>

<style
    lang="scss"
    type="text/scss"
>
@import "shared/assets/scss/box-shadow";

.c-dropdown {
  display: flex;
  max-width: 100%;

  ._trigger {
    display: inline-flex;
    width: 100%;
    color: $font-color-primary;

    &:focus {
      outline: none;
    }
  }

  &.-block {
    display: flex;

    ._trigger {
      display: flex;
      flex: 1 1 auto;
    }
  }
}

._dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;

  &:focus {
    outline: none;
  }

  .__inner {
    .c-card {
      width: 100%;
    }
  }
}
</style>
